import { useCallback, useEffect, useRef } from "react";
import { UseFormSetValue } from "react-hook-form";
import { PackagingType } from "../../../../../../config/enums/PackagingType.enum";
import {
  PACKAGING_DEFAULT_CLICKED_UNIT,
  PACKAGING_TRANSPORT_FIELD
} from "../packagingLevel.config";

interface UsePackagingUnitSaveOptions {
  fieldName: string;
  packagingType: PackagingType;
  unitSizes: number;
  palletWidth?: number;
  palletLength?: number;
  changeUnitsType: string;
  setValue: UseFormSetValue<any>;
  updatePackingDetails: (value: unknown, type: string) => void;
  isSaveBtnClicked: boolean;
  clickedPackagingUnit: string;
  setIsSaveBtnClicked: (value: boolean) => void;
  setClickedPackagingUnit: (value: string) => void;
  setUnitEditMode: (value: boolean) => void;
  onUnitSaveInitiated?: () => void;
}

export const usePackagingUnitSave = ({
  fieldName,
  packagingType,
  unitSizes,
  palletWidth,
  palletLength,
  changeUnitsType,
  setValue,
  updatePackingDetails,
  isSaveBtnClicked,
  clickedPackagingUnit,
  setIsSaveBtnClicked,
  setClickedPackagingUnit,
  setUnitEditMode,
  onUnitSaveInitiated
}: UsePackagingUnitSaveOptions) => {
  const lastEditedTextboxRef = useRef<string | null>(null);
  const prevUnitSizesRef = useRef<number | null>(null);
  const prevPalletWidthRef = useRef<number | undefined>(undefined);
  const prevPalletLengthRef = useRef<number | undefined>(undefined);
  const hasInitializedRef = useRef(false);

  const scrollToLastEditedTextbox = useCallback(() => {
    if (lastEditedTextboxRef.current) {
      const el = document.getElementById(lastEditedTextboxRef.current);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);


  useEffect(() => {
    if (!fieldName) return;

    if (isSaveBtnClicked && fieldName === clickedPackagingUnit) {
      lastEditedTextboxRef.current = fieldName;
      scrollToLastEditedTextbox();
      setIsSaveBtnClicked(false);
      setClickedPackagingUnit(PACKAGING_DEFAULT_CLICKED_UNIT);
      setUnitEditMode(false);
    }
  }, [
    fieldName,
    isSaveBtnClicked,
    clickedPackagingUnit,
    scrollToLastEditedTextbox,
    setIsSaveBtnClicked,
    setClickedPackagingUnit,
    setUnitEditMode
  ]);

  // Sync form from props on mount and when refetch updates saved values.
  useEffect(() => {
    if (!fieldName) return;

    const unitSizesChanged = prevUnitSizesRef.current !== unitSizes;
    if (!hasInitializedRef.current || unitSizesChanged) {
      setValue(fieldName, unitSizes);
      prevUnitSizesRef.current = unitSizes;
    }
    hasInitializedRef.current = true;

    if (
      packagingType === PackagingType.TRANSPORT_UNIT_PACKAGING ||
      clickedPackagingUnit === PACKAGING_TRANSPORT_FIELD
    ) {
      if (prevPalletWidthRef.current !== palletWidth) {
        setValue("palletWidth", palletWidth);
        prevPalletWidthRef.current = palletWidth;
      }
      if (prevPalletLengthRef.current !== palletLength) {
        setValue("palletLength", palletLength);
        prevPalletLengthRef.current = palletLength;
      }
    }
  }, [
    fieldName,
    unitSizes,
    palletWidth,
    palletLength,
    packagingType,
    clickedPackagingUnit,
    setValue
  ]);

  const saveTransportUnitValue = useCallback(
    (palletWidthData: number, palletLengthData: number, palletUnitData: number) => {
      setIsSaveBtnClicked(true);
      setClickedPackagingUnit(fieldName);
      onUnitSaveInitiated?.();
      updatePackingDetails(
        {
          palletWidth: palletWidthData,
          palletLength: palletLengthData,
          packageUnitValue: palletUnitData
        },
        changeUnitsType
      );
      setUnitEditMode(false);
    },
    [
      fieldName,
      changeUnitsType,
      onUnitSaveInitiated,
      setClickedPackagingUnit,
      setIsSaveBtnClicked,
      setUnitEditMode,
      updatePackingDetails
    ]
  );

  const saveUnitValue = useCallback(
    (palletUnitData: number) => {
      setIsSaveBtnClicked(true);
      setClickedPackagingUnit(fieldName);
      onUnitSaveInitiated?.();
      updatePackingDetails(palletUnitData, changeUnitsType);
      setUnitEditMode(false);
    },
    [
      fieldName,
      changeUnitsType,
      onUnitSaveInitiated,
      setClickedPackagingUnit,
      setIsSaveBtnClicked,
      setUnitEditMode,
      updatePackingDetails
    ]
  );

  return {
    saveTransportUnitValue,
    saveUnitValue
  };
};
